import { FC, useEffect, useReducer, ChangeEvent, ReactNode } from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'dibs-react-intl';
import classnames from 'classnames';

import { Input } from 'dibs-elements/exports/Input';
import { Link } from 'dibs-elements/exports/Link';

import PenLine from 'dibs-icons/exports/legacy/PenLine';
import TrashCan from 'dibs-icons/exports/legacy/TrashCan';

import InputWrapper from './InputWrapper';

import {
    SocialNetworkName,
    socialNetworksConfig,
    socialNetworksReducer,
    ReducerState,
    ReducerDispatch,
    PREFIX_HTTPS,
    PREFIX_WWW,
    SAVE_AND_REMOVE_SELECTED_NETWORK,
    CHANGE_USER_INFO,
    INPUT_ERROR,
    SELECT_NETWORK,
    DELETE_NETWORK,
    SocialDetails,
} from './socialNetworksHelpers';

import styles from './SocialNetworks.scss';

const SOCIAL_DETAILS = 'SOCIAL_DETAILS';

const WEBSITE_REGEX =
    /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;

const placeholder = defineMessages({
    example: {
        id: 'SocialNetworks.example',
        defaultMessage: 'E.g.: {url}',
    },
});

// removes empty string values
const getPathSegments = (url: string = ''): string[] => {
    const urlInfo = new URL(url.trim());
    const parts = urlInfo.pathname.split('/').filter(Boolean);
    return parts;
};

const isValidURL = (url: string, baseUrls: readonly string[]): boolean => {
    for (const baseUrl of baseUrls) {
        const baseWithPrefixes = `${PREFIX_HTTPS}${PREFIX_WWW}${baseUrl}`;
        const baseWithoutProtocol = `${PREFIX_WWW}${baseUrl}`;
        if (
            url.startsWith(baseUrl) ||
            url.startsWith(baseWithPrefixes) ||
            url.startsWith(baseWithoutProtocol)
        ) {
            return true;
        }
    }
    return false;
};

const appendPrefixes = (url: string): string => {
    if (!url.startsWith(PREFIX_HTTPS) && !url.startsWith(PREFIX_WWW)) {
        return `${PREFIX_HTTPS}${PREFIX_WWW}${url}`;
    } else if (url.startsWith(PREFIX_WWW)) {
        return `${PREFIX_HTTPS}${url}`;
    }
    return url;
};

const getSocialNetworkInfo = (
    networkName: SocialNetworkName,
    url: string
): { isValid: boolean; name: string | null } => {
    switch (networkName) {
        case 'Instagram': {
            const config = socialNetworksConfig[networkName];
            const { baseUrls, validSegmentsLength } = config;
            if (!isValidURL(url, baseUrls)) {
                return { isValid: false, name: null };
            }
            url = appendPrefixes(url);
            const parts = getPathSegments(url);
            const isValid = parts.length === validSegmentsLength[0];
            const name = (isValid && parts.pop()) || null;
            return {
                isValid,
                name,
            };
        }
        case 'Facebook':
        case 'LinkedIn':
        case 'Houzz': {
            const config = socialNetworksConfig[networkName];
            const { baseUrls, validSegmentsLength } = config;
            if (!isValidURL(url, baseUrls)) {
                return { isValid: false, name: null };
            }
            url = appendPrefixes(url);
            const parts = getPathSegments(url);
            const isValid =
                parts.length === validSegmentsLength[0] || parts.length === validSegmentsLength[1];
            const name = (isValid && parts.pop()) || null;
            return {
                isValid,
                name,
            };
        }
        case 'Website': {
            const isUrl = WEBSITE_REGEX.test(url);
            return isUrl ? { isValid: true, name: url } : { isValid: false, name: null };
        }
        default: {
            return { isValid: false, name: null };
        }
    }
};

const SocialInputSave: FC<{
    hasError?: boolean;
    networkName: SocialNetworkName;
    currentSocialInput?: string | null;
    dispatch: ReducerDispatch;
}> = ({ hasError, networkName, currentSocialInput, dispatch }) => {
    return (
        <Link
            onClick={(): boolean | void => {
                if (!hasError) {
                    // to avoid consistent return lint error,
                    // wrap in condition instead of return false
                    const { isValid } = getSocialNetworkInfo(networkName, currentSocialInput || '');
                    if (isValid) {
                        dispatch({
                            type: SAVE_AND_REMOVE_SELECTED_NETWORK,
                            payload: { savedNetwork: networkName },
                        });
                    }
                }
            }}
            className={`${styles.saveCta} ${hasError && styles.disabledCta}`}
        >
            <FormattedMessage id="SocialNetworks.save" defaultMessage="Save" />
        </Link>
    );
};

const SocialInput: FC<{
    networkName: SocialNetworkName;
    dispatch: ReducerDispatch;
    currentSocialInput?: string | null;
    hasError?: boolean;
}> = ({ networkName, dispatch, currentSocialInput, hasError }) => {
    const intl = useIntl();
    // the SocialInput component will only ever be
    // rendered once at any given time so the
    // dataTn="social-media-username-input" value
    // is fine to keep as a non-dynamic value
    return (
        <InputWrapper>
            <Input
                dataTn="social-media-username-input"
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    const value = e.target.value;
                    const { name, isValid } = getSocialNetworkInfo(networkName, value);
                    if (isValid) {
                        dispatch({ type: INPUT_ERROR, payload: { inputError: null } });
                        dispatch({
                            type: CHANGE_USER_INFO,
                            payload: {
                                socialUserName: name,
                                socialUrl: value,
                            },
                        });
                    } else {
                        dispatch({
                            type: INPUT_ERROR,
                            payload: { inputError: networkName, errorInput: value },
                        });
                    }
                }}
                placeholder={intl.formatMessage(placeholder.example, {
                    url: socialNetworksConfig[networkName].exampleUrl,
                })}
                value={currentSocialInput || ''}
                type="text"
                size="medium"
                hasError={hasError}
                rightDecorator={
                    <SocialInputSave
                        hasError={hasError}
                        networkName={networkName}
                        currentSocialInput={currentSocialInput}
                        dispatch={dispatch}
                    />
                }
            />
        </InputWrapper>
    );
};

type AddSocialNetworkCtaType = {
    networkName: SocialNetworkName;
    dispatch: ReducerDispatch;
    currentNetworkState: ReducerState;
    customLabel?: ReactNode;
};

const SocialNetwork: FC<AddSocialNetworkCtaType> = ({
    networkName,
    dispatch,
    currentNetworkState,
    customLabel,
}) => {
    const { Icon } = socialNetworksConfig[networkName];
    const { selectedNetwork, socialUrl, inputError, errorInput, socialDetailsList } =
        currentNetworkState;
    const currentNetwork = socialDetailsList?.find(i => i.network === networkName);
    const showInput = selectedNetwork === networkName;
    const userNameAdded = !!currentNetwork?.socialUserName;
    const onClick = (): void => {
        dispatch({
            type: SELECT_NETWORK,
            payload: {
                selectedNetwork: networkName,
            },
        });
    };
    const onDelete = (): void => {
        dispatch({
            type: DELETE_NETWORK,
            payload: {
                selectedNetwork: networkName,
            },
        });
    };
    const hasError = inputError === networkName;
    return (
        <>
            {!showInput && (
                <div className={styles.socialNetworkRow}>
                    <Link onClick={onClick} className={styles.iconWrapper}>
                        <Icon />
                    </Link>
                    <Link onClick={onClick} className={styles.addCta}>
                        {userNameAdded
                            ? currentNetwork?.socialUserName || ''
                            : customLabel || networkName}
                    </Link>
                    <div className={styles.iconsContainer}>
                        <Link onClick={onClick} className={styles.iconWrapper}>
                            <PenLine />
                        </Link>
                        {userNameAdded && (
                            <Link onClick={onDelete} className={styles.iconWrapper}>
                                <TrashCan />
                            </Link>
                        )}
                    </div>
                </div>
            )}
            {showInput && (
                <SocialInput
                    networkName={networkName}
                    dispatch={dispatch}
                    // put errorInput first to fix https://1stdibs.atlassian.net/browse/CAM-764
                    currentSocialInput={hasError ? errorInput : socialUrl}
                    hasError={hasError}
                />
            )}
        </>
    );
};

type SocialNetworksList = {
    socialInstructions?: ReactNode;
    customSocialLabels?: { socialNetwork: SocialNetworkName; label: string | ReactNode }[];
    updateForm: (field: string, value: SocialDetails[] | null) => void;
    networks: ('Facebook' | 'Instagram' | 'LinkedIn' | 'Houzz' | 'Website')[];
    showError?: boolean;
    errorMessage?: string | ReactNode;
};

const initialState = {
    selectedNetwork: null,
    socialDetailsList: null,
    savedNetwork: null,
    socialUserName: null,
    socialUrl: null,
    inputError: null,
    errorInput: null,
};

const SocialNetworksList: FC<SocialNetworksList> = ({
    socialInstructions = (
        <FormattedMessage
            id="SocialNetworks.add"
            defaultMessage="Add at least one social network"
        />
    ),
    customSocialLabels,
    updateForm,
    networks,
    showError,
    errorMessage,
}) => {
    const [state, dispatch] = useReducer(socialNetworksReducer, initialState);
    useEffect(() => {
        updateForm(SOCIAL_DETAILS, state.socialDetailsList);
    }, [updateForm, state.socialDetailsList]);
    return (
        <div className={styles.outerWrapper}>
            <div className={classnames(styles.wrapper, { [styles.error]: showError })}>
                <p className={styles.socialInstructions}>{socialInstructions}</p>
                <div>
                    {networks.map(network => {
                        const name = network as SocialNetworkName;
                        const customSocialLabel = customSocialLabels?.find(
                            customSocialLabelObj => name === customSocialLabelObj.socialNetwork
                        )?.label;
                        return (
                            <SocialNetwork
                                key={name}
                                dispatch={dispatch}
                                currentNetworkState={state}
                                networkName={name}
                                customLabel={customSocialLabel}
                            />
                        );
                    })}
                </div>
            </div>
            {showError && <div className={styles.socialsError}>{errorMessage}</div>}
        </div>
    );
};

export default SocialNetworksList;
